import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import '@fontsource/anton';

const Footer = () => {
  return (
    <FooterContainer>
      <FooterWrapper>
        <FooterContent>
          {/* Brand Section */}
          <BrandSection>
            <Logo>NIKE</Logo>
            <Tagline>Just Do It</Tagline>
            <Description>
              Bringing inspiration and innovation to every athlete* in the world.
              <br />
              <Asterisk>*If you have a body, you are an athlete.</Asterisk>
            </Description>
          </BrandSection>

          {/* Products Section */}
          <LinksSection>
            <SectionTitle>Products</SectionTitle>
            <LinksList>
              <FooterLink to="/">Shoes</FooterLink>
              <FooterLink to="/shop">Clothing</FooterLink>
              <FooterLink to="/about">Accessories</FooterLink>
              <FooterLink to="/contact">Sale</FooterLink>
              <FooterLink to="/faqs">New Releases</FooterLink>
            </LinksList>
          </LinksSection>

          {/* Support Section */}
          <LinksSection>
            <SectionTitle>Support</SectionTitle>
            <LinksList>
              <FooterLink to="/contact">Contact Us</FooterLink>
              <FooterLink to="/about">Size Guide</FooterLink>
              <FooterLink to="/">Shipping Info</FooterLink>
              <FooterLink to="/">Returns</FooterLink>
              <FooterLink to="/">Track Order</FooterLink>
            </LinksList>
          </LinksSection>

          {/* Company Section */}
          <LinksSection>
            <SectionTitle>Company</SectionTitle>
            <LinksList>
              <FooterLink to="/about">About Nike</FooterLink>
              <FooterLink to="/">Careers</FooterLink>
              <FooterLink to="/">News</FooterLink>
              <FooterLink to="/">Investors</FooterLink>
              <FooterLink to="/">Sustainability</FooterLink>
            </LinksList>
          </LinksSection>
        </FooterContent>

        {/* Social & Newsletter Section */}
        <FooterSecondary>
          <SocialSection>
            <SectionTitle>Connect With Us</SectionTitle>
            <SocialIcons>
              <SocialIcon href="https://www.instagram.com/nike" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-instagram" />
              </SocialIcon>
              <SocialIcon href="https://twitter.com/nike" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-twitter" />
              </SocialIcon>
              <SocialIcon href="https://www.facebook.com/nike" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-facebook" />
              </SocialIcon>
              <SocialIcon href="https://www.youtube.com/nike" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-youtube" />
              </SocialIcon>
            </SocialIcons>
          </SocialSection>

          <NewsletterSection>
            <SectionTitle>Stay In The Loop</SectionTitle>
            <NewsletterForm>
              <EmailInput type="email" placeholder="Enter your email address" />
              <SubscribeButton>Subscribe</SubscribeButton>
            </NewsletterForm>
            <NewsletterText>
              Be the first to know about new products and exclusive offers.
            </NewsletterText>
          </NewsletterSection>
        </FooterSecondary>

        {/* Footer Bottom */}
        <FooterBottom>
          <BottomLeft>
            <Copyright>© 2024 Nike, Inc. All Rights Reserved</Copyright>
            <CreditLine>
              Designed and developed by{' '}
              <DeveloperLink 
                href="https://gouthammathi.vercel.app" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Goutham Mathi
              </DeveloperLink>{' '}
              with ❤️
            </CreditLine>
          </BottomLeft>
          <BottomRight>
            <LegalLink href="#">Privacy Policy</LegalLink>
            <LegalLink href="#">Terms of Use</LegalLink>
            <LegalLink href="#">Cookie Settings</LegalLink>
          </BottomRight>
        </FooterBottom>
      </FooterWrapper>
    </FooterContainer>
  );
};

export default Footer;

/* Styled Components */
const FooterContainer = styled.footer`
  background: #111;
  color: #fff;
  border-top: 1px solid #333;
`;

const FooterWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px 20px;

  @media (max-width: 768px) {
    padding: 40px 20px 20px;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 40px;
  margin-bottom: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
    gap: 30px;
    text-align: center;
  }
`;

const BrandSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 580px) {
    align-items: center;
  }
`;

const Logo = styled.h1`
  font-family: 'Anton', sans-serif;
  font-size: 32px;
  color: #fff;
  margin: 0 0 8px 0;
  letter-spacing: 2px;
  font-weight: 400;
`;

const Tagline = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0 0 16px 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Description = styled.p`
  font-size: 14px;
  color: #ccc;
  line-height: 1.6;
  margin: 0;
  max-width: 280px;

  @media (max-width: 580px) {
    max-width: 100%;
  }
`;

const Asterisk = styled.span`
  font-style: italic;
  font-size: 12px;
  color: #999;
`;

const LinksSection = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 580px) {
    align-items: center;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  color: #fff;
  margin: 0 0 20px 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LinksList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled(Link)`
  display: block;
  color: #aaa;
  text-decoration: none;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 12px;
  transition: color 0.2s ease;

  &:hover {
    color: #fff;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const FooterSecondary = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  padding: 30px 0;
  border-top: 1px solid #333;
  border-bottom: 1px solid #333;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
    text-align: center;
  }
`;

const SocialSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: #222;
  border-radius: 50%;
  color: #aaa;
  text-decoration: none;
  font-size: 18px;
  transition: all 0.2s ease;

  &:hover {
    background: #333;
    color: #fff;
    transform: translateY(-2px);
  }
`;

const NewsletterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const NewsletterForm = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  background: #222;
  border: 1px solid #333;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: #666;
  }

  &:focus {
    outline: none;
    border-color: #555;
  }

  @media (max-width: 480px) {
    margin-bottom: 8px;
  }
`;

const SubscribeButton = styled.button`
  padding: 12px 24px;
  background: #fff;
  color: #000;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: #f0f0f0;
    transform: translateY(-1px);
  }
`;

const NewsletterText = styled.p`
  font-size: 12px;
  color: #888;
  margin: 0;
  line-height: 1.4;
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 30px;
  font-size: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
`;

const BottomLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const Copyright = styled.p`
  color: #666;
  margin: 0;
`;

const CreditLine = styled.p`
  color: #555;
  margin: 8px 0 0 0;
  font-size: 11px;
  font-weight: 400;
  
  @media (max-width: 768px) {
    text-align: center;
    margin: 12px 0 0 0;
  }
`;

const DeveloperLink = styled.a`
  color: #888;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  border-bottom: 1px solid transparent;
  
  &:hover {
    color: #fff;
    border-bottom-color: #fff;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const BottomRight = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
  }
`;

const LegalLink = styled.a`
  color: #888;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #fff;
  }
`;
